import { billingWebhookIngressObservabilityFromInternals } from "../billing/ingestion/observability/from-internals.ts";
import { recordBillingWebhookIngressQuietly } from "../billing/ingestion/observability/health.ts";
import {
	billingWebhookRegistrationKind,
	publicBillingRejectionCode,
} from "../billing/ingestion/observability/stages.ts";
import {
	BILLING_MOLLIE_CLASSIC_WEBHOOK_PATH,
	BILLING_MOLLIE_EVENTS_WEBHOOK_PATH,
	billingWebhookPathKind,
} from "../billing/ingestion/urls.ts";
import {
	getAthenaClientInternals,
	requireAthenaRootClientInternals,
} from "../runtime/client-internals.ts";
import { MOLLIE_WEBHOOK_MAX_BODY_BYTES } from "../billing/runtime/local/providers/mollie/verify-webhook.ts";
import { compileHttpWebhookIngress } from "../runtime/ingress/compiler/http.ts";
import {
	ATHENA_EVENT_INGRESS_FAILED,
	ATHENA_EVENT_INGRESS_UNAVAILABLE,
	AthenaEventIngressError,
	eventIngressHttpStatus,
	isAthenaEventIngressError,
} from "../runtime/ingress/errors.ts";
import type { EventIngressRuntime } from "../runtime/ingress/runtime.ts";
import { isAthenaHttpBodyLimitError } from "../runtime/transport/http/incoming.ts";

export const DEFAULT_ATHENA_NEXT_BILLING_WEBHOOK_ENDPOINT =
	"/api/athena/billing/webhook";
export const DEFAULT_ATHENA_NEXT_BILLING_MOLLIE_CLASSIC_WEBHOOK_ENDPOINT =
	BILLING_MOLLIE_CLASSIC_WEBHOOK_PATH;
export const DEFAULT_ATHENA_NEXT_BILLING_MOLLIE_EVENTS_WEBHOOK_ENDPOINT =
	BILLING_MOLLIE_EVENTS_WEBHOOK_PATH;

export interface AthenaBillingIngressHandlers {
	POST: (request: Request) => Promise<Response>;
}

export interface CreateAthenaBillingIngressHandlersOptions {
	client: object;
	connectionId?: string;
}

export function createAthenaBillingIngressHandlers(
	options: CreateAthenaBillingIngressHandlersOptions,
): AthenaBillingIngressHandlers {
	requireAthenaRootClientInternals(
		options.client,
		"createAthenaBillingIngressHandlers({ client })",
	);

	return {
		async POST(request: Request): Promise<Response> {
			const internals = getAthenaClientInternals(options.client);
			const observability =
				billingWebhookIngressObservabilityFromInternals(internals);
			const pathname = new URL(request.url).pathname;
			const pathKind = billingWebhookPathKind(pathname);
			const operation =
				pathKind === "classic"
					? "webhook.mollie.classic"
					: pathKind === "events"
						? "webhook.mollie.events"
						: "webhook";
			const kind = billingWebhookRegistrationKind(operation);
			const runtime = internals?.eventIngressRuntime as
				| EventIngressRuntime
				| undefined;
			if (!runtime) {
				const unavailable = new AthenaEventIngressError({
					code: ATHENA_EVENT_INGRESS_UNAVAILABLE,
					message: "EventIngressRuntime is not attached.",
					retryable: true,
				});
				await recordBillingWebhookIngressQuietly(observability, async (store) => {
					await store.recordRejected({
						code: unavailable.code,
						kind,
						stage: "rejected",
						...(options.connectionId
							? { connectionId: options.connectionId }
							: {}),
					});
				});
				return new Response(
					JSON.stringify({
						code: unavailable.code,
						message: unavailable.message,
					}),
					{ headers: { "content-type": "application/json" }, status: 503 },
				);
			}
			try {
				const ingress = await compileHttpWebhookIngress({
					request,
					domain: "billing",
					operation,
					connectionId: options.connectionId,
					maxBodyBytes: MOLLIE_WEBHOOK_MAX_BODY_BYTES,
				});
				await recordBillingWebhookIngressQuietly(observability, async (store) => {
					const connectionId = ingress.connectionId ?? options.connectionId;
					await store.recordStage({
						kind,
						occurredAt: new Date(),
						stage: "received",
						ingressId: ingress.id,
						operation,
						provider: "mollie",
						...(connectionId ? { connectionId } : {}),
						...(ingress.correlationId
							? { correlationId: ingress.correlationId }
							: {}),
						...(ingress.traceId ? { traceId: ingress.traceId } : {}),
					});
					if (connectionId) {
						await store.recordDelivery({
							connectionId,
							event: "received",
							kind,
						});
					}
				});
				const result = await runtime.ingest(ingress);
				const reconciliation = result.reconciliation ?? "skipped";
				return new Response(
					JSON.stringify({
						ok: true,
						duplicate: result.duplicate,
						reconciliation,
					}),
					{
						headers: { "content-type": "application/json" },
						status: result.duplicate ? 200 : 202,
					},
				);
			} catch (error) {
				if (isAthenaHttpBodyLimitError(error)) {
					return new Response(
						JSON.stringify({
							code: error.code,
							message: error.message,
						}),
						{
							headers: { "content-type": "application/json" },
							status: error.status,
						},
					);
				}
				const status = eventIngressHttpStatus(error);
				const code = isAthenaEventIngressError(error)
					? error.code
					: ATHENA_EVENT_INGRESS_FAILED;
				console.error("[athena.billing] webhook ingress rejected", error);
				await recordBillingWebhookIngressQuietly(observability, async (store) => {
					await store.recordRejected({
						code: publicBillingRejectionCode(error),
						kind,
						stage: "rejected",
						...(options.connectionId
							? { connectionId: options.connectionId }
							: {}),
					});
				});
				return new Response(
					JSON.stringify({
						code,
						message: isAthenaEventIngressError(error)
							? error.code
							: "Webhook ingest failed.",
					}),
					{
						headers: { "content-type": "application/json" },
						status,
					},
				);
			}
		},
	};
}
